import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium",
    "transition-all duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1",
    "disabled:pointer-events-none disabled:opacity-40",
  ].join(" "),
  {
    variants: {
      variant: {
        /* Primary — clear amber, not too heavy */
        default: [
          "bg-primary text-white",
          "shadow-[0_1px_0_rgba(255,255,255,0.20)_inset,0_1px_6px_hsl(28_70%_45%/0.22)]",
          "hover:brightness-[1.06] hover:shadow-[0_1px_0_rgba(255,255,255,0.20)_inset,0_3px_10px_hsl(28_70%_45%/0.30)]",
          "active:scale-[0.98] active:brightness-95",
        ].join(" "),

        /* Destructive */
        destructive: [
          "bg-destructive text-white",
          "shadow-[0_1px_0_rgba(255,255,255,0.15)_inset,0_1px_6px_hsl(4_72%_55%/0.22)]",
          "hover:brightness-[1.06]",
          "active:scale-[0.98]",
        ].join(" "),

        /* Outline — clean white glass, light border */
        outline: [
          "border border-black/10 text-foreground/70",
          "bg-white/75 backdrop-blur-sm",
          "shadow-[0_1px_0_rgba(255,255,255,0.95)_inset,0_1px_3px_rgba(0,0,0,0.05)]",
          "hover:bg-white/95 hover:text-foreground hover:border-black/14 hover:shadow-[0_1px_0_rgba(255,255,255,1)_inset,0_2px_6px_rgba(0,0,0,0.07)]",
          "active:scale-[0.98]",
        ].join(" "),

        /* Secondary — very light warm grey */
        secondary: [
          "bg-secondary text-secondary-foreground border border-black/6",
          "shadow-[0_1px_0_rgba(255,255,255,0.80)_inset]",
          "hover:bg-secondary/70 hover:border-black/8",
          "active:scale-[0.98]",
        ].join(" "),

        /* Ghost — transparent, shows on hover */
        ghost: [
          "text-foreground/55",
          "hover:text-foreground/80 hover:bg-black/[0.04]",
          "active:scale-[0.98]",
        ].join(" "),

        /* Link */
        link: "text-primary underline-offset-4 hover:underline p-0 h-auto",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm:      "h-7 rounded-md px-3 text-xs",
        lg:      "h-10 rounded-xl px-6",
        icon:    "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size:    "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
